/**
 * GrantAddon.jsx (iter146) — Superadmin UI for the /api/admin/billing/* endpoints.
 *
 * Wraps the grant_addon.py CLI script behind a button + dropdown. Use cases:
 *   • Unblock a paying tenant whose Stripe webhook failed to fire
 *   • Comp a free trial / partner / internal tenant manually
 *   • Audit historic grants from the same surface
 *
 * Hidden behind platform_role='superadmin'. Non-superadmins see a 403
 * empty state because the backend endpoints all 403 — we surface the
 * upstream error directly rather than mock a paywall.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Crown, Gift, Trash, ArrowClockwise, MagnifyingGlass, Receipt, ShieldCheck,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtMoney = (n) => {
  if (n == null) return '—';
  return Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-AU', { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return iso; }
};

const PLAN_OPTIONS = [
  { key: 'starter', label: 'Starter' },
  { key: 'grow', label: 'Grow' },
  { key: 'core', label: 'Core' },
  { key: 'scale', label: 'Scale' },
  { key: 'business', label: 'Business' },
  { key: 'enterprise', label: 'Enterprise' },
];

function ChangePlanCard({ tenant, onChanged }) {
  const [planKey, setPlanKey] = useState(tenant.plan_key || 'business');
  const [billingCycle, setBillingCycle] = useState(tenant.billing_cycle || 'monthly');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  // Reset whenever the operator picks a different tenant
  useEffect(() => {
    setPlanKey(tenant.plan_key || 'business');
    setBillingCycle(tenant.billing_cycle || 'monthly');
    setNotes('');
  }, [tenant.id, tenant.plan_key, tenant.billing_cycle]);

  const dirty = planKey !== tenant.plan_key || (billingCycle && billingCycle !== tenant.billing_cycle);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/admin/billing/change-plan`, {
        company_id: tenant.id,
        plan_key: planKey,
        billing_cycle: billingCycle,
        notes: notes || null,
      }, { withCredentials: true });
      if (r.data?.no_change) {
        toast.message('No change — same plan + cycle');
      } else {
        toast.success(`Plan changed: ${r.data.before.plan_key} → ${r.data.after.plan_key}`);
        onChanged?.(r.data.after);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Plan change failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-card border border-dashed border-brand/40 px-3 py-3 text-xs" data-testid="change-plan-card">
      <div className="overline text-[10px] mb-2 flex items-center justify-between">
        <span>Change plan (sales-led · skip Stripe)</span>
        <span className="font-mono text-muted-foreground normal-case tracking-normal">
          current: <b className="uppercase">{tenant.plan_key}</b>
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2 mb-2">
        <div>
          <Label className="overline text-[9px]" htmlFor="cp-plan">New plan</Label>
          <select
            id="cp-plan"
            value={planKey}
            onChange={(e) => setPlanKey(e.target.value)}
            data-testid="change-plan-select"
            className="mt-1 w-full border border-border bg-background px-2 py-1.5 text-sm font-mono uppercase"
          >
            {PLAN_OPTIONS.map((p) => (
              <option key={p.key} value={p.key}>{p.label}</option>
            ))}
          </select>
        </div>
        <div>
          <Label className="overline text-[9px]" htmlFor="cp-cycle">Billing cycle</Label>
          <select
            id="cp-cycle"
            value={billingCycle}
            onChange={(e) => setBillingCycle(e.target.value)}
            data-testid="change-plan-cycle"
            className="mt-1 w-full border border-border bg-background px-2 py-1.5 text-sm font-mono uppercase"
          >
            <option value="monthly">Monthly</option>
            <option value="annual">Annual</option>
          </select>
        </div>
      </div>
      <Input
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Note (audit log)"
        data-testid="change-plan-notes"
        className="mb-2 text-xs"
      />
      <Button
        size="sm"
        onClick={submit}
        disabled={busy || !dirty}
        data-testid="change-plan-submit"
        className="w-full bg-brand hover:bg-brand/90 text-white font-bold"
      >
        {busy ? 'Updating…' : dirty ? `Change to ${planKey.toUpperCase()}` : 'No change'}
      </Button>
    </div>
  );
}


export default function GrantAddon() {
  const [catalog, setCatalog] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [tenantsTotal, setTenantsTotal] = useState(0);
  const [tenantSearch, setTenantSearch] = useState('');
  const [tenantsBusy, setTenantsBusy] = useState(false);

  const [selectedTenant, setSelectedTenant] = useState(null);
  const [emailLookup, setEmailLookup] = useState('');
  const [emailBusy, setEmailBusy] = useState(false);

  const [selectedAddon, setSelectedAddon] = useState('customer_pricing');
  const [action, setAction] = useState('grant');
  const [reason, setReason] = useState('manual grant via admin UI');
  const [busy, setBusy] = useState(false);

  const [audit, setAudit] = useState([]);
  const [error, setError] = useState('');

  const loadCatalog = async () => {
    try {
      const r = await axios.get(`${API}/api/admin/billing/addon-catalog`, { withCredentials: true });
      setCatalog(r.data || []);
    } catch (e) {
      if (e?.response?.status === 403) setError('forbidden');
      else toast.error(e?.response?.data?.detail || 'Could not load addon catalog');
    }
  };

  const loadTenants = async (q = '') => {
    setTenantsBusy(true);
    try {
      const r = await axios.get(`${API}/api/admin/billing/tenants`, {
        params: { search: q || undefined, limit: 200 }, withCredentials: true,
      });
      // iter156 — backend now returns {items, total, limit}. Also tolerate the
      // older plain-array response for back-compat during rolling deploys.
      if (Array.isArray(r.data)) {
        setTenants(r.data);
        setTenantsTotal(r.data.length);
      } else {
        setTenants(r.data?.items || []);
        setTenantsTotal(r.data?.total ?? (r.data?.items?.length || 0));
      }
    } catch (e) {
      if (e?.response?.status === 403) setError('forbidden');
    } finally {
      setTenantsBusy(false);
    }
  };

  const loadAudit = async () => {
    try {
      const r = await axios.get(`${API}/api/admin/billing/audit-trail`, {
        params: { limit: 25 }, withCredentials: true,
      });
      setAudit(r.data || []);
    } catch (e) { /* swallow — top-level error already shown */ }
  };

  useEffect(() => { loadCatalog(); loadAudit(); }, []);

  // iter157 — When opened from the Recent Signups page, the URL carries
  // ?email=<owner_email>. Auto-run the email lookup so the operator lands
  // with the tenant pre-selected.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const prefilled = searchParams.get('email');
    if (!prefilled) return;
    setEmailLookup(prefilled);
    // Defer lookup until next tick so the input shows the value first.
    setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/api/admin/billing/tenant-by-email`, {
          params: { email: prefilled.trim() }, withCredentials: true,
        });
        setSelectedTenant(r.data.company);
        toast.success(`Found ${r.data.company.name}`);
      } catch (e) {
        toast.error(`Could not auto-select tenant for ${prefilled} — search manually below.`);
      }
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // iter156 — live debounced search. Previously the search only ran on
  // Enter, so typing into the box did nothing visible. New users assumed
  // their company didn't exist (when actually it just hadn't been searched yet).
  // Mount-time empty query also pulls the initial unfiltered list — so we
  // don't need a separate loadTenants('') on mount.
  useEffect(() => {
    const t = setTimeout(() => { loadTenants(tenantSearch); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantSearch]);

  const lookupByEmail = async () => {
    if (!emailLookup.includes('@')) {
      toast.error('Enter a valid email.');
      return;
    }
    setEmailBusy(true);
    try {
      const r = await axios.get(`${API}/api/admin/billing/tenant-by-email`, {
        params: { email: emailLookup.trim() }, withCredentials: true,
      });
      setSelectedTenant(r.data.company);
      toast.success(`Found ${r.data.company.name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No tenant for that email');
    } finally {
      setEmailBusy(false);
    }
  };

  const addonDef = useMemo(
    () => catalog.find((a) => a.key === selectedAddon) || null,
    [catalog, selectedAddon],
  );

  const already = !!(selectedTenant && selectedTenant.addons?.includes(selectedAddon));

  const submit = async () => {
    if (!selectedTenant) {
      toast.error('Pick a tenant first.');
      return;
    }
    if (!selectedAddon) {
      toast.error('Pick an add-on.');
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/admin/billing/grant-addon`, {
        company_id: selectedTenant.id,
        addon_key: selectedAddon,
        action,
        reason: reason || (action === 'grant' ? 'manual grant via admin UI' : 'manual revoke via admin UI'),
      }, { withCredentials: true });
      if (!r.data.changed) {
        toast.message(`No change: ${r.data.reason?.replace(/_/g, ' ')}.`);
      } else {
        toast.success(`${action === 'grant' ? 'Granted' : 'Revoked'} · ${addonDef?.name || selectedAddon}`);
      }
      // Refresh tenant addons + audit
      const updated = { ...selectedTenant, addons: r.data.addons_after || selectedTenant.addons };
      setSelectedTenant(updated);
      await Promise.all([loadAudit(), loadTenants(tenantSearch)]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not apply change');
    } finally {
      setBusy(false);
    }
  };

  if (error === 'forbidden') {
    return (
      <Layout>
        <div className="p-12 text-center max-w-lg mx-auto" data-testid="grant-addon-forbidden">
          <ShieldCheck size={48} className="text-amber-600 mx-auto mb-4" weight="fill" />
          <h1 className="text-2xl font-bold mb-2">Superadmin only</h1>
          <p className="text-sm text-muted-foreground">
            The Grant Add-on console is restricted to platform superadmins. Your account does not have that role.
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="grant-addon-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <Crown size={28} weight="fill" className="text-amber-600" /> Grant Add-on
            </h1>
            <p className="text-muted-foreground mt-1 text-sm">
              Superadmin tool · grant or revoke billing add-ons for any tenant. Every change is audit-logged.
            </p>
          </div>
          <Button variant="outline" onClick={() => { loadCatalog(); loadTenants(tenantSearch); loadAudit(); }} className="rounded-none" data-testid="grant-refresh">
            <ArrowClockwise size={14} className="mr-2" /> Refresh
          </Button>
        </div>

        {/* Two-column composer */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Tenant picker */}
          <div className="bg-card border border-border" data-testid="tenant-picker">
            <div className="overline px-4 py-3 border-b border-border">Step 1 · Pick tenant</div>
            <div className="p-4 space-y-4">
              <div>
                <Label className="overline" htmlFor="email-lookup">Look up by user email</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    id="email-lookup"
                    type="email"
                    value={emailLookup}
                    onChange={(e) => setEmailLookup(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') lookupByEmail(); }}
                    placeholder="admin@stockaris.com"
                    className="font-mono"
                    data-testid="email-lookup-input"
                  />
                  <Button onClick={lookupByEmail} disabled={emailBusy} className="bg-brand hover:bg-brand/90 text-white rounded-none" data-testid="email-lookup-btn">
                    {emailBusy ? '…' : 'Look up'}
                  </Button>
                </div>
              </div>

              <div>
                <Label className="overline" htmlFor="tenant-search">Or search by company name / slug</Label>
                <div className="relative mt-1">
                  <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  <Input
                    id="tenant-search"
                    value={tenantSearch}
                    onChange={(e) => setTenantSearch(e.target.value)}
                    placeholder="Type to filter — newest tenants shown first"
                    className="pl-8"
                    data-testid="tenant-search-input"
                    autoComplete="off"
                  />
                </div>
                <div className="text-[11px] text-muted-foreground mt-1" data-testid="tenant-count">
                  {tenantsBusy ? 'Searching…' : (
                    tenants.length === tenantsTotal
                      ? `${tenantsTotal} tenant${tenantsTotal === 1 ? '' : 's'}${tenantSearch ? ' match' : ''}`
                      : `Showing ${tenants.length} of ${tenantsTotal} — refine your search to narrow down`
                  )}
                </div>
              </div>

              <div className="border border-border max-h-[280px] overflow-y-auto" data-testid="tenant-list">
                <table className="w-full text-sm">
                  <thead className="bg-muted text-xs uppercase tracking-widest text-muted-foreground sticky top-0">
                    <tr>
                      <th className="text-left p-2">Name</th>
                      <th className="text-left p-2">Plan</th>
                      <th className="text-left p-2">Add-ons</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tenantsBusy ? (
                      <tr><td colSpan={3} className="p-4 text-center text-xs text-muted-foreground">Loading…</td></tr>
                    ) : tenants.length === 0 ? (
                      <tr><td colSpan={3} className="p-4 text-center text-xs text-muted-foreground">No tenants match.</td></tr>
                    ) : tenants.map((t) => (
                      <tr
                        key={t.id}
                        onClick={() => setSelectedTenant(t)}
                        className={`border-t border-border cursor-pointer hover:bg-brand/5 ${selectedTenant?.id === t.id ? 'bg-brand/10' : ''}`}
                        data-testid={`tenant-row-${t.slug || t.id}`}
                      >
                        <td className="p-2">
                          <div className="font-bold text-xs">{t.name}</div>
                          {t.slug && <div className="font-mono text-[10px] text-muted-foreground">{t.slug}</div>}
                        </td>
                        <td className="p-2 text-xs uppercase font-bold">{t.plan_key}</td>
                        <td className="p-2 text-[11px] font-mono text-muted-foreground">
                          {t.addons?.length ? t.addons.join(', ') : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {selectedTenant && (
                <div className="bg-brand/5 border border-brand/20 px-3 py-2 text-xs" data-testid="selected-tenant">
                  Selected: <b>{selectedTenant.name}</b> ({selectedTenant.plan_key})
                  <div className="font-mono text-[10px] text-muted-foreground mt-1">
                    Active add-ons: {selectedTenant.addons?.length ? selectedTenant.addons.join(', ') : '(none)'}
                  </div>
                </div>
              )}

              {/* iter162 — Change Plan (sales-led, skip-Stripe) */}
              {selectedTenant && (
                <ChangePlanCard
                  tenant={selectedTenant}
                  onChanged={(after) => {
                    setSelectedTenant({
                      ...selectedTenant,
                      plan_key: after.plan_key,
                      billing_cycle: after.billing_cycle,
                    });
                    loadTenants(tenantSearch);
                    loadAudit();
                  }}
                />
              )}
            </div>
          </div>

          {/* Right: Addon + action composer */}
          <div className="bg-card border border-border" data-testid="addon-composer">
            <div className="overline px-4 py-3 border-b border-border">Step 2 · Apply add-on</div>
            <div className="p-4 space-y-4">
              <div>
                <Label className="overline" htmlFor="addon-select">Add-on</Label>
                <select
                  id="addon-select"
                  value={selectedAddon}
                  onChange={(e) => setSelectedAddon(e.target.value)}
                  className="mt-1 w-full text-sm px-3 py-2 border border-border bg-background"
                  data-testid="addon-select"
                >
                  {catalog.map((a) => (
                    <option key={a.key} value={a.key}>
                      {a.name} · {fmtMoney(a.price_monthly_usd)}/mo
                    </option>
                  ))}
                </select>
                {addonDef && (
                  <div className="text-[11px] text-muted-foreground mt-2 leading-snug" data-testid="addon-meta">
                    {addonDef.tagline}
                    <div className="font-mono mt-1">
                      Unlocks: {addonDef.feature_unlocks?.length ? addonDef.feature_unlocks.join(', ') : '(no feature flags)'}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <Label className="overline">Action</Label>
                <div className="mt-1 inline-flex border border-border w-full">
                  {['grant', 'revoke'].map((a) => (
                    <button
                      key={a}
                      type="button"
                      onClick={() => setAction(a)}
                      className={`flex-1 text-xs px-3 py-2 uppercase font-bold tracking-widest border-r border-border last:border-r-0 ${action === a ? (a === 'grant' ? 'bg-emerald-600 text-white' : 'bg-rose-600 text-white') : 'bg-background text-muted-foreground hover:bg-muted/50'}`}
                      data-testid={`action-${a}`}
                    >
                      {a === 'grant' ? <><Gift size={11} className="inline mr-1" /> Grant</> : <><Trash size={11} className="inline mr-1" /> Revoke</>}
                    </button>
                  ))}
                </div>
                {selectedTenant && (
                  <div className="text-[11px] text-muted-foreground mt-2">
                    Current state for this tenant:{' '}
                    <b className={already ? 'text-emerald-700' : 'text-rose-700'}>
                      {already ? `already has ${selectedAddon}` : `does not have ${selectedAddon}`}
                    </b>
                  </div>
                )}
              </div>

              <div>
                <Label className="overline" htmlFor="reason">Audit reason</Label>
                <Textarea
                  id="reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={2}
                  placeholder="e.g. locked-out admin grant · partner comp · Stripe webhook failed"
                  className="mt-1"
                  data-testid="reason-input"
                />
              </div>

              <Button
                onClick={submit}
                disabled={busy || !selectedTenant || (action === 'grant' && already) || (action === 'revoke' && !already)}
                className={`w-full rounded-none text-white ${action === 'grant' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-600 hover:bg-rose-700'}`}
                data-testid="apply-btn"
              >
                {busy ? 'Applying…' : action === 'grant'
                  ? (already ? `${addonDef?.name || 'Add-on'} already active` : `Grant ${addonDef?.name || 'add-on'}`)
                  : (already ? `Revoke ${addonDef?.name || 'add-on'}` : `${addonDef?.name || 'Add-on'} not active`)}
              </Button>
            </div>
          </div>
        </div>

        {/* Audit trail */}
        <div className="bg-card border border-border" data-testid="audit-trail-panel">
          <div className="overline px-4 py-3 border-b border-border flex items-center gap-2">
            <Receipt size={14} weight="fill" /> Recent grants & revokes
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted text-xs uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="text-left p-3">When</th>
                  <th className="text-left p-3">Tenant</th>
                  <th className="text-left p-3">Add-on</th>
                  <th className="text-left p-3">Action</th>
                  <th className="text-left p-3">Actor</th>
                  <th className="text-left p-3">Source</th>
                  <th className="text-left p-3">Reason</th>
                </tr>
              </thead>
              <tbody>
                {audit.length === 0 ? (
                  <tr><td colSpan={7} className="p-6 text-center text-xs text-muted-foreground" data-testid="audit-empty">No grant history yet.</td></tr>
                ) : audit.map((r) => (
                  <tr key={r.id} className="border-t border-border" data-testid={`audit-row-${r.id}`}>
                    <td className="p-3 text-[11px] font-mono text-muted-foreground">{fmtDate(r.at)}</td>
                    <td className="p-3 text-xs font-bold">{r.company_name || r.company_id}</td>
                    <td className="p-3 text-xs font-mono">{r.addon_key}</td>
                    <td className="p-3">
                      <span className={`text-[10px] px-2 py-0.5 border uppercase tracking-widest font-bold ${r.action === 'grant' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200'}`}>
                        {r.action}
                      </span>
                    </td>
                    <td className="p-3 text-xs font-mono text-muted-foreground">{r.actor || '—'}</td>
                    <td className="p-3 text-[10px] uppercase tracking-widest text-muted-foreground">{r.source}</td>
                    <td className="p-3 text-xs text-muted-foreground truncate max-w-[280px]">{r.reason || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}
